import * as THREE from 'three';
import { SWEEP_MAX_SUBSTEPS, SWEEP_STEP_M } from '../config/constants';
import { intersectSegmentCylinder, intersectSegmentSphere, pointInsideCylinder } from './raycast';

/**
 * A single surface contact of the blade segment against a shape.
 * `tAlongSegment` and `local` describe *where* on the blade / on the shape it touched.
 */
export type ShapeHit = {
  /** World-space contact point on the shape surface. */
  point: THREE.Vector3;
  /** Position of the contact along the blade, 0 = base (pommel), 1 = tip (punta). */
  tAlongSegment: number;
  /** Shape-local descriptors the caller maps to domain meaning (e.g. { localY } → zone). */
  local?: Record<string, number>;
};

/**
 * A shape the blade can be swept against. The sweep core depends only on this interface,
 * so future modes add a shape by implementing it — no change to the sweep itself.
 */
export type Collider = {
  /** First surface crossing of the segment base→tip, or null if it does not cross. */
  intersectSegment(base: THREE.Vector3, tip: THREE.Vector3): ShapeHit | null;
  /** Whether a world point lies inside the closed volume (used for the inside/outside latch). */
  containsPoint?(p: THREE.Vector3): boolean;
};

/** Blade pose for a single instant: pommel (base) and punta (tip) in world space. */
export type BladePose = {
  base: THREE.Vector3;
  tip: THREE.Vector3;
};

/** Result of a swept blade query: a surface contact plus when within the frame it happened. */
export type SweepHit = ShapeHit & {
  /** Fraction within the frame interval at which contact first occurred, 0 = prev, 1 = cur. */
  sweepT: number;
};

const _base = new THREE.Vector3();
const _tip = new THREE.Vector3();

/**
 * Sweep the full blade segment from `prev` pose to `cur` pose against a collider,
 * returning the earliest surface contact within the frame (or null).
 *
 * The blade is interpolated through adaptive substeps so a fast swing is detected at
 * the true entry surface instead of skipping past it ("tunneling"). Substep count
 * scales with the larger endpoint displacement; with no previous pose only `cur` is tested.
 *
 * Pure: no side effects. The outside→inside latch is the caller's responsibility.
 */
export function sweepBladeAgainst(
  prev: BladePose | null,
  cur: BladePose,
  collider: Collider,
): SweepHit | null {
  if (!prev) {
    const hit = collider.intersectSegment(cur.base, cur.tip);
    return hit ? { ...hit, sweepT: 1 } : null;
  }

  const baseDisp = prev.base.distanceTo(cur.base);
  const tipDisp = prev.tip.distanceTo(cur.tip);
  const maxDisp = Math.max(baseDisp, tipDisp);
  const steps = Math.min(SWEEP_MAX_SUBSTEPS, Math.max(1, Math.ceil(maxDisp / SWEEP_STEP_M)));

  // Walk from prev → cur; return the first substep that crosses the surface.
  for (let i = 1; i <= steps; i++) {
    const alpha = i / steps;
    _base.copy(prev.base).lerp(cur.base, alpha);
    _tip.copy(prev.tip).lerp(cur.tip, alpha);
    const hit = collider.intersectSegment(_base, _tip);
    if (hit) return { ...hit, sweepT: alpha };
  }
  return null;
}

/**
 * Whether the blade at a single pose is inside the collider — a surface crossing OR
 * an endpoint buried in the volume. Used by the caller to re-arm the outside→inside latch.
 */
export function bladeInsideCollider(
  collider: Collider,
  base: THREE.Vector3,
  tip: THREE.Vector3,
): boolean {
  if (collider.intersectSegment(base, tip)) return true;
  if (collider.containsPoint?.(base)) return true;
  if (collider.containsPoint?.(tip)) return true;
  return false;
}

/**
 * Vertical capped cylinder (axis +Y, base at `base`, given height/radius).
 * Lateral surface via `intersectSegmentCylinder`; the top cap is added here so a steep
 * fendente onto the crown reports localY = height (→ head zone) instead of a degenerate value.
 * The bottom is left open (the pell stands on the floor). `local` carries `{ localY }`.
 */
export function cylinderCollider(base: THREE.Vector3, heightM: number, radiusM: number): Collider {
  return {
    intersectSegment(segBase, segTip): ShapeHit | null {
      const lateral = intersectSegmentCylinder(segBase, segTip, base, heightM, radiusM);
      const segLen = segBase.distanceTo(segTip);

      let bestT = Infinity;
      let best: ShapeHit | null = null;

      // Guard against the near-parallel degenerate branch, which can report a localY
      // outside [0, height]; a crown strike should fall through to the cap below.
      if (lateral && lateral.localY >= -1e-6 && lateral.localY <= heightM + 1e-6) {
        const t = segLen > 1e-9 ? lateral.tAlongSegment / segLen : 0;
        bestT = t;
        best = {
          point: lateral.point,
          tAlongSegment: clamp01(t),
          local: { localY: lateral.localY },
        };
      }

      // Top cap: intersect the segment with the plane y = base.y + height, inside the radius.
      const dy = segTip.y - segBase.y;
      if (Math.abs(dy) > 1e-9) {
        const capY = base.y + heightM;
        const t = (capY - segBase.y) / dy;
        if (t >= 0 && t <= 1 && t < bestT) {
          const px = segBase.x + (segTip.x - segBase.x) * t;
          const pz = segBase.z + (segTip.z - segBase.z) * t;
          const dx = px - base.x;
          const dz = pz - base.z;
          if (dx * dx + dz * dz <= radiusM * radiusM) {
            best = {
              point: new THREE.Vector3(px, capY, pz),
              tAlongSegment: clamp01(t),
              local: { localY: heightM },
            };
          }
        }
      }

      return best;
    },
    containsPoint(p): boolean {
      return pointInsideCylinder(p, base, heightM, radiusM);
    },
  };
}

/**
 * Sphere collider (center + radius). Uses closest-approach so a thrust that stops inside
 * still registers (no plane to cross). `local` carries `{ distanceFromCenter }`.
 */
export function sphereCollider(center: THREE.Vector3, radiusM: number): Collider {
  return {
    intersectSegment(segBase, segTip): ShapeHit | null {
      const hit = intersectSegmentSphere(segBase, segTip, center, radiusM);
      if (!hit) return null;
      const abx = segTip.x - segBase.x;
      const aby = segTip.y - segBase.y;
      const abz = segTip.z - segBase.z;
      const lenSq = abx * abx + aby * aby + abz * abz;
      const t =
        lenSq > 1e-12
          ? clamp01(
              ((hit.point.x - segBase.x) * abx +
                (hit.point.y - segBase.y) * aby +
                (hit.point.z - segBase.z) * abz) /
                lenSq,
            )
          : 0;
      return {
        point: hit.point,
        tAlongSegment: t,
        local: { distanceFromCenter: hit.distanceFromCenter },
      };
    },
    containsPoint(p): boolean {
      return p.distanceTo(center) <= radiusM;
    },
  };
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}
